import { Controller } from "@hotwired/stimulus";
import { checkOnlineStatus } from "../../onlineStatus";
import {
  getPendingEntries,
  getPendingCount,
  markEntryStatus,
  deleteEntry,
  ENTRY_STATUS,
} from "../../db/harvestEntries";
import { post as railsPost } from "@rails/request.js";
import * as Turbo from "@hotwired/turbo";

// Connects to data-controller="harvest-entries--sync"
export default class extends Controller {
  static targets = [
    "badge",
    "count",
    "tbody",
    "pendingRowTemplate",
    "pendingTableWrapper",
    "emptyState",
  ];
  static values = {
    farmId: Number,
    syncUrl: String,
    offlineFlashMessage: String,
  };

  connect() {
    this.#abortController = new AbortController();

    window.addEventListener("online", () => this.#onConnectivityRestored(), {
      signal: this.#abortController.signal,
    });

    this.#showOfflineFlash();
    this.#renderOptimisticRows();
  }

  disconnect() {
    this.#abortController.abort();
  }

  #abortController;

  #refreshBadge(count) {
    if (!this.hasBadgeTarget) return;

    if (count > 0) {
      this.badgeTarget.classList.remove("hidden");
      this.badgeTarget.classList.add("flex");
      if (this.hasCountTarget) {
        this.countTarget.textContent = count;
      }
    } else {
      this.badgeTarget.classList.add("hidden");
      this.badgeTarget.classList.remove("flex");
    }
  }

  async #renderOptimisticRows() {
    const tbody = this.hasTbodyTarget ? this.tbodyTarget : null;

    tbody
      ?.querySelectorAll("[data-optimistic-row]")
      .forEach((el) => el.remove());

    const entries = await getPendingEntries(this.farmIdValue);
    this.#refreshBadge(entries.length);

    if (entries.length === 0) return;

    if (this.hasPendingTableWrapperTarget) {
      this.pendingTableWrapperTarget.classList.remove("hidden");
    }
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("hidden");
    }

    if (!tbody || !this.hasPendingRowTemplateTarget) return;

    for (const entry of entries) {
      if (!entry.display) continue;
      const row = this.#buildRowFromTemplate(entry);
      tbody.prepend(row);
    }
  }

  #buildRowFromTemplate(entry) {
    const clone =
      this.pendingRowTemplateTarget.content.firstElementChild.cloneNode(true);
    clone.dataset.optimisticRow = "true";

    const slotMap = {};
    clone
      .querySelectorAll("[data-slot]")
      .forEach((el) => (slotMap[el.dataset.slot] = el));

    for (const [key, value] of Object.entries(entry.display)) {
      if (slotMap[key]) slotMap[key].textContent = value || "";
    }

    return clone;
  }

  #showOfflineFlash() {
    if (!sessionStorage.getItem("harvestEntryOfflineFlash")) return;
    sessionStorage.removeItem("harvestEntryOfflineFlash");

    const flash = document.getElementById("flash");
    if (!flash) return;

    const alert = document.createElement("div");
    alert.className = "alert";
    alert.dataset.controller = "removals";

    const icon = document.createElement("i");
    icon.className = "w-6 h-6 i-ph-check-circle-fill text-primary";

    const text = document.createElement("span");
    text.textContent = this.offlineFlashMessageValue;

    alert.appendChild(icon);
    alert.appendChild(text);
    flash.appendChild(alert);
  }

  async #onConnectivityRestored() {
    const isOnline = await checkOnlineStatus();
    if (!isOnline) return;

    await this.#syncAll();
  }

  async #syncAll() {
    const entries = await getPendingEntries(this.farmIdValue);
    if (entries.length === 0) return;

    let synced = 0;

    for (const entry of entries) {
      const success = await this.#syncEntry(entry);
      if (success) synced++;
    }

    this.#refreshBadge(
      (await getPendingCount(this.farmIdValue))
    );

    if (synced > 0) {
      Turbo.visit(window.location.href);
    }
  }

  async #syncEntry(entry) {
    await markEntryStatus(entry.id, ENTRY_STATUS.SYNCING);

    try {
      const formData = new FormData();
      for (const [key, value] of Object.entries(entry.form_data)) {
        formData.append(key, value);
      }

      const response = await railsPost(this.syncUrlValue, {
        body: formData,
      });

      if (response.ok) {
        await deleteEntry(entry.id);
        return true;
      } else if (response.statusCode === 422) {
        await markEntryStatus(
          entry.id,
          ENTRY_STATUS.FAILED,
          "Server validation failed"
        );
        return false;
      } else {
        await markEntryStatus(
          entry.id,
          ENTRY_STATUS.FAILED,
          `Server error ${response.statusCode}`
        );
        return false;
      }
    } catch (err) {
      await markEntryStatus(entry.id, ENTRY_STATUS.PENDING, err.message);
      return false;
    }
  }
}
